import { c as _c } from "react/compiler-runtime";
import { Alert, Box, colors, Padding, Text } from '@newfront-insurance/core-ui';
import { useContext, useEffect, useRef, type JSX } from 'react';
import styled from 'styled-components';
import { Result } from './result';
import { Suggestion } from './suggestion';
import type { SearchResult } from '../../../../bff/types';
import { bugsMessage } from '../../../utils';
import { SearchContext } from '../context';
interface Props {
  onRecentlySearchedClick: (value: string) => void;
  onRenderPreview: (result: SearchResult, containerId?: string) => JSX.Element;
  onSearchResultClick: (result: SearchResult, rank: number) => unknown;
  onSearchSuggestionClick: (suggestion: string) => Promise<void>;
}
export function Popout(t0) {
  const $ = _c(38);
  const {
    onRecentlySearchedClick,
    onRenderPreview,
    onSearchResultClick,
    onSearchSuggestionClick
  } = t0;
  const {
    activeResultIndex,
    currentQuery,
    error,
    recentlySearched,
    recentlyVisited,
    results,
    setActiveResultIndex,
    suggestions
  } = useContext(SearchContext);
  const dvRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== activeResultIndex) {
    t1 = () => {
      if (activeResultIndex === 0) {
        dvRef.current?.scrollTo({
          left: 0,
          top: 0,
          behavior: "smooth"
        });
      }
    };
    t2 = [activeResultIndex];
    $[0] = activeResultIndex;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (error) {
    const customMessage = `${error.statusText}: ${error.data?.errorMessage || error.data?.errors?.[0] || ""}`;
    const t3 = `${error.data ? customMessage : "There was a problem getting the search results."}`;
    let t4;
    if ($[3] !== t3) {
      t4 = bugsMessage(t3);
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t4) {
      t5 = <StyledContainer><Padding x={24} y={12}><Alert><Text>{t4}</Text></Alert></Padding></StyledContainer>;
      $[5] = t4;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  const hasRecentlySearched = recentlySearched.length > 0;
  const hasRecentlyVisited = recentlyVisited.length > 0;
  const hasResults = results && results.length > 0;
  const hasSuggestions = suggestions && suggestions.length > 0;
  if ((hasRecentlySearched || hasRecentlyVisited) && !currentQuery) {
    let t3;
    if ($[7] !== hasRecentlySearched || $[8] !== hasRecentlyVisited || $[9] !== onRecentlySearchedClick || $[10] !== recentlySearched) {
      t3 = hasRecentlySearched && <Padding left={24} bottom={hasRecentlyVisited ? 24 : 12}><Box><Text size="small" textAlign="left" weight={300} color={colors.steel[400]}>Recently searched</Text>{recentlySearched.map(item => <Box key={item} onClick={() => onRecentlySearchedClick(item)}><Text marginTop={12} marginBottom={4} weight={400}>{item}</Text></Box>)}</Box></Padding>;
      $[7] = hasRecentlySearched;
      $[8] = hasRecentlyVisited;
      $[9] = onRecentlySearchedClick;
      $[10] = recentlySearched;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== hasRecentlyVisited || $[13] !== onRenderPreview || $[14] !== onSearchResultClick || $[15] !== recentlyVisited) {
      t4 = hasRecentlyVisited && <Box><Padding left={24} x={12}><Text size="small" textAlign="left" weight={300} color={colors.steel[400]}>Recently visited</Text></Padding>{recentlyVisited.map((result, index) => <Result key={result.id} result={result} onClick={() => onSearchResultClick(result, index)} renderPreview={onRenderPreview} />)}</Box>;
      $[12] = hasRecentlyVisited;
      $[13] = onRenderPreview;
      $[14] = onSearchResultClick;
      $[15] = recentlyVisited;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t3 || $[18] !== t4) {
      t5 = <StyledContainer>{t3}{t4}</StyledContainer>;
      $[17] = t3;
      $[18] = t4;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    return t5;
  }
  if (!results || !currentQuery) {
    return null;
  }
  const t3 = hasResults ? "460px" : "auto";
  let t4;
  if ($[20] !== t3) {
    t4 = {
      minHeight: t3
    };
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== currentQuery || $[23] !== hasSuggestions || $[24] !== onSearchSuggestionClick || $[25] !== suggestions) {
    t5 = hasSuggestions && <Padding top={hasSuggestions ? 8 : 0} x={24}>{suggestions?.filter(suggestion => suggestion !== currentQuery).map(suggestion_0 => <Suggestion key={suggestion_0} currentQuery={currentQuery} suggestion={suggestion_0} onClick={() => onSearchSuggestionClick(suggestion_0)} />)}</Padding>;
    $[22] = currentQuery;
    $[23] = hasSuggestions;
    $[24] = onSearchSuggestionClick;
    $[25] = suggestions;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== activeResultIndex || $[28] !== hasResults || $[29] !== onRenderPreview || $[30] !== onSearchResultClick || $[31] !== results || $[32] !== setActiveResultIndex) {
    t6 = hasResults ? <ul role="listbox" aria-labelledby="global-search-label" style={{
      padding: 0
    }}>{results.map((result_0, i) => <Result isActiveResult={activeResultIndex === i} key={result_0.id} result={result_0} onClick={() => onSearchResultClick(result_0, i)} onHover={() => setActiveResultIndex(i)} renderPreview={onRenderPreview} />)}</ul> : <Padding size={24}><Box><Text weight={400} textAlign="center" color="secondary">Sorry we couldn’t find any matches</Text><Text weight={400} textAlign="center" color="light">Try other search terms</Text></Box></Padding>;
    $[27] = activeResultIndex;
    $[28] = hasResults;
    $[29] = onRenderPreview;
    $[30] = onSearchResultClick;
    $[31] = results;
    $[32] = setActiveResultIndex;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== t4 || $[35] !== t5 || $[36] !== t6) {
    t7 = <StyledContainer style={t4} ref={dvRef}>{t5}{t6}</StyledContainer>;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  return t7;
}
const StyledContainer = styled.div`
  background: #fff;
  border-top: 1px solid rgba(190, 199, 208, 0.5);
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  box-sizing: border-box;
  left: 0;
  max-height: calc(100vh - 260px);
  overflow-y: overlay;
  overflow-x: hidden;
  padding: 12px 0;
  position: relative;
  width: 100%;
  z-index: 3;

  &::-webkit-scrollbar-track {
    background-color: #fff;
    position: absolute;
    border-bottom-right-radius: 4px;
  }

  &::-webkit-scrollbar {
    width: 10px;
    background-color: ${colors.steel[200]};
  }

  &::-webkit-scrollbar-thumb {
    width: 10px;
    border-radius: 10px;
    background: ${colors.steel[200]};
    border: 2px solid #fff;
  }
`;